import AbstractFilterControl from "./AbstractFilterControl";

import type LayerViewModel from "@esWebAPI/es/map/LayerViewModel";
import type { Attribute } from "@esWebAPI/es/model/Feature";
import type { Criteria } from "@esWebAPI/es/model/Criteria";

const LIKE_CRITERIA = {$type: "Like"}; // only for searching, constant value.
const NON_VALUE_TYPE = ["Is_Empty", "Is_Not_Empty"];
const NEGATIVE_LOGIC_OPERATORS = ["Not_Equals", "Is_Not_Empty", "Does_Not_Contain"];

type SubFilterType = "Equals" | "Is_Empty" | "Not_Equals" | "Is_Not_Empty" | "Contains" | "Does_Not_Contain";
type SubFilter = {
    type: SubFilterType,
    value?: any,
    hasValue: boolean
}

export default class DropdownFilterControl extends AbstractFilterControl {
    private subFilters: SubFilter[];
    //range;
    //minValue: number;
    //maxValue: number;

    constructor(layerViewModel: LayerViewModel, attribute: Attribute, range?: any) {
        super(layerViewModel, "Dropdown", attribute);

        this.subFilters = [];
        //this.range = range;
        //this.minValue = range?.min ?? 0;
        //this.maxValue = range?.max ?? 0;
    }

    getSubFilters() {
        return this.subFilters;
    }

    toCriteria(): Criteria {
        const criteria = this.getCriterias();

        if(criteria.length === 1) {
            return criteria[0];
        }

        const isNegativeLogic = this.subFilters.findIndex( c => NEGATIVE_LOGIC_OPERATORS.includes(c.type)) !== -1;
        return {
            $type: isNegativeLogic ? "And" : "Or",
            criteria: criteria
        };
    }

    isNegativeLogic(): boolean {
        return this.subFilters.findIndex( c => NEGATIVE_LOGIC_OPERATORS.includes(c.type)) !== -1;
    }

    fromCriteria(criteria: Criteria | Criteria[]): undefined {
        if (Array.isArray(criteria)) { // assuming multiple criteria
            this.subFilters = criteria.reduce((res, crit) => {
                if (crit.$type === "Or") {
                    Array.prototype.push.apply(res, crit.criteria.map((crit) => this.getSubFilter(crit)));
                } else {
                    res.push(this.getSubFilter(crit));
                }
                return res;
            }, [] as SubFilter[]);
        } else if (criteria.$type === "Or") {
            this.subFilters = criteria.criteria.map((crit) => this.getSubFilter(crit));
        } else {
            this.subFilters = [this.getSubFilter(criteria)];
        }
    }

    private getSubFilter(criteria: Criteria): SubFilter | null {
        let type: string;
        let value;
        if (criteria.$type === "Not") {
            if (criteria.criteria.$type !== "Equal" && criteria.criteria.$type !== "Like") {
                es.log.error("Unsupported criteria type for Dropdown Filter", criteria);
                return null;
            }

            type = criteria.criteria.$type === "Equal" ? "NotEqual" : "NotLike";
            value = criteria.criteria.value;
        } else {
            type = criteria.$type;
            value = criteria.value;
        }

        const subType = this.getFilterSubType(type, value);
        return {
            type: subType,
            value: value,
            hasValue: !NON_VALUE_TYPE.includes(subType)
        };
    }

    private getFilterSubType(type: string, value: any): SubFilterType | null {
        switch (type) {
            case "Equal":
                return value ? "Equals" : "Is_Empty";
            case "NotEqual":
                return value ? "Not_Equals" : "Is_Not_Empty";
            case "Like":
                return "Contains";
            case "NotLike":
                return "Does_Not_Contain";
            default:
                es.log.error("Unsupported criteria type for Dropdown Filter", type, value);
                return null;
        }
    }

    isValid(): boolean {
        return this.getActiveSubFilter().length > 0;
    }

    private createCriteria(operator: string, value: any) {
        switch (operator) {
            case 'Contains':
                return {
                    '$type': 'Like',
                    'attr': this.attrName,
                    'value': value
                };

            case 'Does_Not_Contain':
                return {
                    '$type': 'Not',
                    'criteria': {
                        '$type': 'Like',
                        'attr': this.attrName,
                        'value': value
                    }
                };

            case 'Equals':
                return {
                    '$type': 'Equal',
                    'attr': this.attrName,
                    'value': value
                };

            case 'Not_Equals':
                return {
                    '$type': 'NotEqual',
                    'attr': this.attrName,
                    'value': value
                };

            case 'Is_Empty':
                return {
                    '$type': 'Equal',
                    'attr': this.attrName,
                    'value': null
                };

            case 'Is_Not_Empty':
                return {
                    '$type': 'NotEqual',
                    'attr': this.attrName,
                    'value': null
                };
            default:
                throw new Error("IllegalState"); // forgot some case?!
        }
    }

    getCriterias() {
        return this.getActiveSubFilter().map((filter) => {
            return this.createCriteria(filter.type, filter.value);
        }, this);
    }

    /**
     * Get "active" subfilter. A subFilter is considered active if its value (if any) is set.
     * If the user hasn't provide a value yet, the subfilter should not be transformed into a criteria, so it's considered inactive
     * @returns {Array.<T>}
     */
    getActiveSubFilter(): SubFilter[] {
        return this.subFilters.filter((filter) => {
            return !filter.hasValue || !!filter.value;
        });
    }

    changeValue(value: any, index: number) {
        if (this.subFilters[index]){
            this.subFilters[index].value = value;
            this.update(this.subFilters);
        }
    }

    changeType(type: SubFilterType, index: number) {
        if (this.subFilters[index]) {
            this.subFilters[index].type = type;
            this.subFilters[index].hasValue = !NON_VALUE_TYPE.includes(type);
            this.update(this.subFilters);
        }
    }

    addDropdown(type: SubFilterType, value: any) {
        this.subFilters.push({
            type: type,
            value: value,
            hasValue: !NON_VALUE_TYPE.includes(type)
        });

        this.eventChannel.fireEvent("onUpdate");
    }

    removeDropdown(index: number) {
        this.subFilters.splice(index, 1);

        this.eventChannel.fireEvent("onUpdate");
    }

    update(subFilters: SubFilter[], triggerRender = true) {
        this.subFilters = subFilters.map((filter) => {
            return $.extend(filter, {
                hasValue: !NON_VALUE_TYPE.includes(filter.type)
            });
        });

        this.eventChannel?.fireEvent("onUpdate", triggerRender); // there maybe no eventChannel if the filter is getting destroyed
    }

    match(filter: AbstractFilterControl): boolean {
        return this !== filter
            && this.attrName === filter.getAttrName()
            && this.isLocked() === filter.isLocked()
            && (filter.getType() === "Checkbox" || filter.getType() === "Dropdown");
    }

    merge(filter: AbstractFilterControl) {
        const criteria = [this.toCriteria(true), filter.toCriteria(true)];
        this.fromCriteria(criteria);
    }

    static supportCriteria(criteria: Criteria | Criteria[], attribute: Attribute | Attribute[]): boolean {
        if (criteria.$type === "Or" && criteria.criteria &&  Array.isArray(criteria.criteria) && criteria.criteria.length === 0) {
            return true;
        }

        if (criteria.$type === "And" && criteria.criteria &&  Array.isArray(criteria.criteria) && criteria.criteria.length) {
            return false;
        }

        if (!attribute || Array.isArray(attribute)) {
            return false;
        }

        if (criteria.criteria && Array.isArray(criteria.criteria) && !criteria.criteria.every((crit) => crit.$type !== "Or" && crit.$type !== "And")) {
            return false;
        }

        return DropdownFilterControl.isMultipleFilter(criteria, attribute) || DropdownFilterControl.isDropdownFilter(criteria, attribute);
    }

    private static isMultipleFilter(criteria: Criteria | Criteria[], attribute: Attribute): boolean {
        return Array.isArray(criteria) && criteria.some((crit) => {
            return DropdownFilterControl.isDropdownFilter(crit, attribute);
        });
    }

    private static isDropdownFilter(criteria: Criteria | Criteria[], attribute: Attribute): boolean {
        if (Array.isArray(criteria)) {
            return false;
        }

        //var attrRange = range[attribute.name];
        var hasLike = es.util.criteria.find(criteria, LIKE_CRITERIA) !== null; // Only dropdown filter support like
        return DropdownFilterControl.supportAttribute(attribute.attrType) || hasLike;
    }
    
    static supportAttribute(attrType: string): boolean {
        return attrType === "STRING";
    }

    static fromCriteria(layerViewModel: LayerViewModel, criteria: Criteria, attribute: Attribute): JQueryDeferred<DropdownFilterControl> {
        return new DropdownFilterControl(layerViewModel, attribute).init().done((filter: DropdownFilterControl) => {
            filter.fromCriteria(criteria);
        });
    }

    // es.control.DropdownFilter = DropdownFilter;
}
